import { useCallback, useEffect, useId, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { AVISO_PRE, CANAL, direccionVacia } from "@/lib/registro";
import { useRegistryRefdata } from "@/components/registry/useRegistryRefdata";
import AddressFields, { AvisoTablas } from "@/components/registry/AddressFields";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const inputCls = "mt-1 bg-[#F7F5F0] border-[#DCD8D0]";
const selectCls = "mt-1 h-10 w-full rounded-md border border-[#DCD8D0] bg-[#F7F5F0] px-3 text-sm";
const ENTORNOS = ["PRE", "PRO"];
const nuevo = () => ({ id: "", name: "", nip: "", ses_code: "", is_default: false, address: direccionVacia() });
const esObjeto = (v) => Boolean(v) && typeof v === "object" && !Array.isArray(v);

// Lo guardado puede no validar (`stored_errors`): a un establecimiento al que le falta algo se le ponen los campos
// vacíos para poder editarlo. Lo que tiene se envía tal cual: si no vale, el 422 dice qué falla.
function normalizar(d) {
  const ests = Array.isArray(d.establishments) ? d.establishments : [];
  return {
    ...d,
    establishments: ests.map((raw) => {
      const e = esObjeto(raw) ? raw : {};
      const base = nuevo();
      const out = Object.fromEntries(Object.keys(base).map((k) => [k, e[k] ?? base[k]]));
      return { ...out, address: esObjeto(e.address) ? e.address : base.address };
    }),
  };
}

// Municipio de cada establecimiento tal como lo conoce el servidor: si se cambia, el canal ya no es el que dijo.
const municipios = (d) => Object.fromEntries((Array.isArray(d.establishments) ? d.establishments : [])
  .filter((e) => esObjeto(e) && e.id).map((e) => [e.id, String(e.address?.municipality_code || "")]));

// `channels[id]` es `null` en dos casos: el País Vasco (sin canal) y una oficina guardada que no valida
// (`broken_ids`, que no se comunica hasta corregirla).
function textoCanal(e, s, municipioGuardado) {
  const canales = s.channels;
  const codigo = String(e.address?.municipality_code || "");
  if (!e.id || !esObjeto(canales) || !(e.id in canales) || codigo !== municipioGuardado) return "Se calcula al guardar";
  if ((s.broken_ids || []).includes(e.id)) return "Ninguno hasta que corrijas sus errores";
  if (canales[e.id]) return CANAL[canales[e.id]] || canales[e.id];
  return "Sin canal: País Vasco (Ertzaintza), pendiente hasta que haya un cliente allí";
}

function Oficina({ e, i, canal, refdata, onSet, onQuitar }) {
  const uid = useId();
  const id = (k) => `${uid}-${k}`;
  return (
    <div className="border border-[#DCD8D0] rounded-lg p-4 space-y-3" data-testid={`rs-est-${i}`}>
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="sm:col-span-2">
          <Label htmlFor={id("name")}>Nombre (hasta 40 caracteres)</Label>
          <Input id={id("name")} className={inputCls} maxLength={40} value={e.name} onChange={(ev) => onSet("name", ev.target.value)}
            data-testid={`rs-est-${i}-name`} />
        </div>
        <div className="flex items-end gap-3">
          <div className="flex items-center gap-2 h-10">
            <Checkbox id={id("default")} checked={!!e.is_default} onCheckedChange={(v) => onSet("is_default", Boolean(v))}
              data-testid={`rs-est-${i}-default`} />
            <Label htmlFor={id("default")}>Predeterminada</Label>
          </div>
          <Button variant="ghost" className="text-[#B54434]" onClick={onQuitar} aria-label={`Quitar la oficina ${i + 1}`}
            data-testid={`rs-est-${i}-remove`}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
        <div>
          <Label htmlFor={id("nip")}>NIP de los Mossos</Label>
          <Input id={id("nip")} className={inputCls} inputMode="numeric" value={e.nip}
            onChange={(ev) => onSet("nip", ev.target.value.replace(/\D/g, "").slice(0, 9))} data-testid={`rs-est-${i}-nip`} />
        </div>
        <div>
          <Label htmlFor={id("ses")}>Código de establecimiento de SES</Label>
          <Input id={id("ses")} className={inputCls} maxLength={10} value={e.ses_code} onChange={(ev) => onSet("ses_code", ev.target.value)}
            data-testid={`rs-est-${i}-ses`} />
        </div>
        <div>
          <span className="text-sm font-medium leading-none">Canal</span>
          <div className="mt-2 text-sm text-[#5C5C5C]" data-testid={`rs-est-${i}-channel`}>{canal}</div>
        </div>
      </div>
      <AddressFields value={e.address} onChange={(a) => onSet("address", a)} refdata={refdata} soloEspana avisarTablas={false}
        testid={`rs-est-${i}-addr`} />
    </div>
  );
}

export default function RegistrySettings() {
  const refdata = useRegistryRefdata(true);
  const uid = useId();
  const id = (k) => `${uid}-${k}`;
  const [s, setS] = useState(null);
  const [guardados, setGuardados] = useState({});
  const [entornoGuardado, setEntornoGuardado] = useState(null);
  const [saving, setSaving] = useState(false);
  const [probando, setProbando] = useState(false);
  const [siguiente, setSiguiente] = useState("");
  const aplicar = useCallback((d) => {
    setS(normalizar(d));
    setGuardados(municipios(d));
    setEntornoGuardado(d?.mossos_env ?? null);
  }, []);
  useEffect(() => {
    api.get("/admin/registry/settings")
      .then(({ data }) => { aplicar(data); setSiguiente(String(data.counter?.next || 1)); })
      .catch((e) => toast.error(formatError(e)));
  }, [aplicar]);
  const set = (k, v) => setS((d) => ({ ...d, [k]: v }));
  const setEst = (i, k, v) => setS((d) => ({
    ...d,
    establishments: d.establishments.map((e, j) => {
      if (j === i) return { ...e, [k]: v };
      return k === "is_default" && v ? { ...e, is_default: false } : e;
    }),
  }));
  const guardar = async () => {
    setSaving(true);
    try {
      const { enabled, mossos_env, mossos_notifications, establishments, ses_landlord_code, ses_application } = s;
      aplicar((await api.put("/admin/registry/settings", { enabled, mossos_env, mossos_notifications, establishments,
        ses_landlord_code, ses_application })).data);
      toast.success("Ajustes del registro guardados");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };
  // Solo el contador: lo que aún no se ha guardado del resto de la página se queda como está.
  const guardarContador = async () => {
    try {
      const { data } = await api.put("/admin/registry/counter", { year: s.counter.year, next: Number(siguiente) });
      setS((d) => ({ ...d, counter: data.counter }));
      setSiguiente(String(data.counter?.next || 1));
      toast.success("Contador actualizado");
    } catch (err) {
      toast.error(formatError(err));
    }
  };
  const probar = async () => {
    setProbando(true);
    try {
      const { data } = await api.post("/admin/registry/test-connection");
      const texto = `Mossos (${data.environment}): ${data.ok ? "la conexión funciona" : "no ha funcionado"}`
        + `${data.http_status ? ` · HTTP ${data.http_status}` : ""}. ${data.message || ""}`;
      if (data.ok) toast.success(texto); else toast.error(texto);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setProbando(false);
    }
  };
  const cred = s?.credentials || {};
  // El aviso de PRE habla de Canal Empresa: solo si alguna oficina guardada es de los Mossos.
  const hayMossos = esObjeto(s?.channels) && Object.values(s.channels).includes("mossos");
  // Un entorno guardado que no es PRE ni PRO se enseña tal cual y no se cambia solo: hay que elegir uno para guardar.
  const entorno = typeof s?.mossos_env === "string" ? s.mossos_env : String(s?.mossos_env ?? "");
  const entornoValido = ENTORNOS.includes(entorno);
  let textoSes = "apagado: el XML se descarga y se sube a mano";
  if (cred.ses_ws_enabled) textoSes = cred.ses_ws ? "activado" : "activado, pero faltan sus credenciales";
  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <header className="bg-white border-b border-[#DCD8D0]">
        <div className="container-x py-4"><Link to="/admin" className="inline-flex items-center text-[#5C5C5C] hover:text-marca"><ArrowLeft className="h-4 w-4 mr-2" /> Panel</Link></div>
      </header>
      <main className="container-x py-8 max-w-4xl space-y-6">
        <h1 className="font-display text-3xl font-bold tracking-tight text-[#1A1A1A]">Registro de viajeros</h1>
        <p className="text-sm text-[#5C5C5C]">
          El Real Decreto 933/2021 obliga a comunicar cada reserva y cada entrega en 24 horas: a los Mossos d'Esquadra si
          la oficina está en Cataluña y al Ministerio del Interior (SES.HOSPEDAJES) en el resto de España.{" "}
          <Link to="/admin/registro-viajeros" className="underline text-marca">Ver las comunicaciones</Link>
        </p>
        {s && (
          <>
            {/* Mientras lo guardado sea PRE (y en cuanto se elige): lo enviado allí no cuenta como comunicado. */}
            {hayMossos && (entornoGuardado === "PRE" || s.mossos_env === "PRE") && (
              <div className="rounded-xl border border-[#E89B33]/40 bg-[#E89B33]/10 p-4 text-sm text-[#7A4E0E]" data-testid="rs-pre">
                {AVISO_PRE}
              </div>
            )}
            {s.stored_invalid && (
              <div role="alert" className="rounded-xl border border-acento/40 bg-acento/10 p-4 text-sm text-[#8C3B26]" data-testid="rs-stored-errors">
                <p className="font-medium">
                  {s.stored_blocked ? "El registro está bloqueado hasta que corrijas:" : "Estas oficinas no se comunican hasta que corrijas:"}
                </p>
                <ul className="list-disc pl-5 mt-1 space-y-1">
                  {(s.stored_errors || []).map((m, i) => <li key={i}>{m}</li>)}
                </ul>
              </div>
            )}
            {s.demo && <div className="rounded-xl border border-[#E89B33]/40 bg-[#E89B33]/10 p-4 text-sm text-[#7A4E0E]">Demostración: no se envía nada a los Mossos.</div>}
            <section className="bg-white border border-[#DCD8D0] rounded-xl p-6 grid sm:grid-cols-2 gap-4" data-testid="rs-general">
              <div className="sm:col-span-2 flex items-center gap-2">
                <Checkbox id={id("enabled")} checked={!!s.enabled} onCheckedChange={(v) => set("enabled", Boolean(v))} data-testid="rs-enabled" />
                <Label htmlFor={id("enabled")}>Registro activado</Label>
              </div>
              <div>
                <Label htmlFor={id("env")}>Entorno de los Mossos</Label>
                <select id={id("env")} className={selectCls} value={entorno} onChange={(e) => set("mossos_env", e.target.value)}
                  aria-describedby={entornoValido ? undefined : id("env-ayuda")} data-testid="rs-env">
                  {!entornoValido && <option value={entorno} disabled>{entorno ? `${entorno} ` : ""}(valor guardado no válido)</option>}
                  <option value="PRE">Pruebas (PRE)</option>
                  <option value="PRO">Real (PRO)</option>
                </select>
                {!entornoValido && <p id={id("env-ayuda")} className="mt-1 text-xs text-[#8C3B26]">Elige el entorno para poder guardar.</p>}
              </div>
              <div className="flex items-center gap-2 sm:mt-6">
                <Checkbox id={id("notif")} checked={!!s.mossos_notifications} onCheckedChange={(v) => set("mossos_notifications", Boolean(v))}
                  data-testid="rs-notifications" />
                <Label htmlFor={id("notif")} className="font-normal leading-snug">Avisos de los Mossos por correo (pendiente de confirmar con su soporte)</Label>
              </div>
            </section>
            <section className="bg-white border border-[#DCD8D0] rounded-xl p-6 space-y-4" data-testid="rs-establishments">
              <div className="flex items-center justify-between">
                <h2 className="font-display text-lg font-semibold text-[#1A1A1A]">Oficinas (establecimientos)</h2>
                <Button variant="outline" onClick={() => set("establishments", [...s.establishments, nuevo()])} data-testid="rs-add-est"><Plus className="h-4 w-4 mr-2" /> Añadir</Button>
              </div>
              {s.establishments.length === 0 && <p className="text-sm text-[#5C5C5C]">Añade la oficina donde se recogen los vehículos: su provincia decide a quién se comunica.</p>}
              {s.establishments.length > 0 && <AvisoTablas refdata={refdata} />}
              {s.establishments.map((e, i) => (
                <Oficina key={e.id || `nuevo-${i}`} e={e} i={i} refdata={refdata} canal={textoCanal(e, s, guardados[e.id])}
                  onSet={(k, v) => setEst(i, k, v)} onQuitar={() => set("establishments", s.establishments.filter((_, j) => j !== i))} />
              ))}
            </section>
            <section className="bg-white border border-[#DCD8D0] rounded-xl p-6 grid sm:grid-cols-2 gap-4" data-testid="rs-ses">
              <div>
                <Label htmlFor={id("landlord")}>Código de arrendador de SES</Label>
                <Input id={id("landlord")} className={inputCls} maxLength={10} value={s.ses_landlord_code ?? ""} onChange={(e) => set("ses_landlord_code", e.target.value)}
                  data-testid="rs-ses-landlord" />
              </div>
              <div>
                <Label htmlFor={id("application")}>Aplicación (servicio web de SES)</Label>
                <Input id={id("application")} className={inputCls} maxLength={50} value={s.ses_application ?? ""} onChange={(e) => set("ses_application", e.target.value)}
                  data-testid="rs-ses-application" />
              </div>
            </section>
            <Button onClick={guardar} disabled={saving || !entornoValido} aria-describedby={entornoValido ? undefined : id("env-ayuda")}
              className="bg-marca hover:bg-marca-hover text-white" data-testid="rs-save">{saving ? "Guardando…" : "Guardar"}</Button>
            <section className="bg-white border border-[#DCD8D0] rounded-xl p-6 space-y-3" data-testid="rs-counter">
              <h2 className="font-display text-lg font-semibold text-[#1A1A1A]">Número de registro ({s.counter?.year})</h2>
              <p className="text-sm text-[#5C5C5C]">Correlativo de contratos que piden los Mossos; vuelve a 1 cada año. Si ya llevabas un libro, pon el siguiente número que toca (solo se puede subir).</p>
              <div className="flex items-end gap-3">
                <div>
                  <Label htmlFor={id("counter")}>Siguiente número</Label>
                  <Input id={id("counter")} type="number" min="1" className={inputCls} value={siguiente} onChange={(e) => setSiguiente(e.target.value)}
                    data-testid="rs-counter-next" />
                </div>
                <Button variant="outline" onClick={guardarContador} data-testid="rs-counter-save">Guardar contador</Button>
              </div>
            </section>
            <section className="bg-white border border-[#DCD8D0] rounded-xl p-6 space-y-3" data-testid="rs-credentials">
              <h2 className="font-display text-lg font-semibold text-[#1A1A1A]">Credenciales</h2>
              <ul className="text-sm text-[#5C5C5C] space-y-1">
                <li>Mossos, pruebas (PRE): {cred.mossos_pre ? "puestas" : "faltan"}</li>
                <li>Mossos, real (PRO): {cred.mossos_pro ? "puestas" : "faltan"}</li>
                <li>SES, servicio web: {textoSes}</li>
              </ul>
              <p className="text-xs text-[#8A8A8A]">Las pone Avant en el servidor con ops/claves-registro.sh; nunca se escriben en el panel.</p>
              <Button variant="outline" onClick={probar} disabled={probando} data-testid="rs-test">{probando ? "Probando…" : "Probar conexión con los Mossos"}</Button>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
